import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { portalApi } from "@/lib/portalApi";
import { ClientChat, useChatUnreadCount } from "@/components/portal/ClientChat";
import { getPortalSupabase } from "@/lib/portalSupabase";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, Circle, ChevronLeft, MessageSquare, Paperclip, Upload, Download, Send, AlertTriangle, Eye, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useConfirm } from "@/lib/confirmContext";

const parseDate = (d: string) => new Date(d.includes('T') ? d : d + 'T00:00:00');

const formatFileSize = (bytes: number) =>
  bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

const STATUS_ICONS: Record<string, React.ReactNode> = {
  done: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  open: <Circle className="w-4 h-4 text-muted-foreground" />,
  in_progress: <Circle className="w-4 h-4 text-blue-500" />,
  on_hold: <Circle className="w-4 h-4 text-yellow-500" />,
};

function TaskRow({ task, projectQueryKey }: { task: any; projectQueryKey: string[] }) {
  const [open, setOpen] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const askConfirm = useConfirm();
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
  // ODN-8: one attachment at a time asks to confirm its delete.
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<
    Array<{ key: string; name: string; status: "success" | "duplicate" | "error"; message?: string }>
  >([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Staff completion writes "done"; "completed" kept as tolerance for any
  // dev DB created without the project_tasks_status_check constraint.
  const isCompleted = task.status === "done" || task.status === "completed";

  const completeMutation = useMutation({
    mutationFn: (completed: boolean) => portalApi.completeTask(task.id, completed),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectQueryKey });
    },
    onError: (err: any) => {
      toast({ title: "Failed to update task", description: err.message, variant: "destructive" });
    },
  });

  const { data: comments, refetch: refetchComments } = useQuery({
    queryKey: ["portal", "comments", task.id],
    queryFn: () => portalApi.comments(task.id),
    enabled: open,
  });

  const { data: attachments, refetch: refetchAttachments } = useQuery({
    queryKey: ["portal", "attachments", task.id],
    queryFn: () => portalApi.attachments(task.id),
    enabled: open,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const supabase = await getPortalSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const filePath = `${session.user.id}/${task.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from("portal-uploads")
        .upload(filePath, file, { upsert: false });

      if (uploadError) throw uploadError;

      return portalApi.registerAttachment(task.id, {
        name: file.name,
        file_path: filePath,
        file_size: file.size,
        file_type: file.type || undefined,
      });
    },
    onSuccess: () => {
      toast({ title: "File uploaded successfully" });
      refetchAttachments();
      // The "mark it complete?" nudge deliberately does NOT live here.
      // mutateAsync awaits this callback and handleFiles uploads a batch
      // sequentially, so prompting here would block the loop on the dialog and
      // ask once per file. It fires once, after the batch - see handleFiles.
    },
    onError: (err: any) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const commentMutation = useMutation({
    mutationFn: (body: string) => portalApi.postComment(task.id, body),
    onSuccess: () => {
      setCommentText("");
      refetchComments();
      toast({ title: "Message sent" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to send message", description: err.message, variant: "destructive" });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: (attachmentId: string) => portalApi.downloadUrl(task.id, attachmentId),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (err: any) => {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    },
  });

  const viewMutation = useMutation({
    mutationFn: (attachmentId: string) => portalApi.downloadUrl(task.id, attachmentId, "view"),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (err: any) => {
      toast({ title: "Couldn't open file", description: err.message, variant: "destructive" });
    },
  });

  // ODN-8: a client may remove a file THEY uploaded. Firm files get no control
  // here (and the server refuses them regardless — see canClientDeleteAttachment).
  // The delete is permanent: row + stored blob, matching the staff-side ENG-73
  // delete, hence the inline confirm step before it fires.
  const deleteMutation = useMutation({
    mutationFn: (attachmentId: string) => portalApi.deleteAttachment(task.id, attachmentId),
    onSuccess: () => {
      setConfirmingDeleteId(null);
      // Refetch rather than filtering locally so the list stays sourced from the
      // server's own from_firm split.
      refetchAttachments();
      toast({ title: "File deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Delete failed", description: err.message, variant: "destructive" });
    },
  });

  // PDFs and images render in a browser tab; everything else is download-only.
  const isViewable = (a: { name: string; file_type: string | null }) =>
    /(^application\/pdf$)|(^image\/)/.test(a.file_type ?? "") || /\.(pdf|jpe?g|png|gif|webp)$/i.test(a.name);

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    setUploadResults([]);
    let uploaded = 0;

    for (const file of Array.from(files)) {
      const key = `${file.name}-${file.size}-${Date.now()}`;
      const isDuplicate = attachments?.some(
        (a) => !a.from_firm && a.name === file.name && a.file_size === file.size
      );

      if (isDuplicate) {
        setUploadResults((prev) => [...prev, { key, name: file.name, status: "duplicate" }]);
        toast({
          title: "Already uploaded",
          description: `You already uploaded ${file.name}`,
          variant: "destructive",
        });
        continue;
      }

      setUploadingKeys((prev) => new Set(prev).add(key));
      try {
        await uploadMutation.mutateAsync(file);
        uploaded++;
        setUploadResults((prev) => [...prev, { key, name: file.name, status: "success" }]);
      } catch (err: any) {
        setUploadResults((prev) => [...prev, { key, name: file.name, status: "error", message: err?.message }]);
      } finally {
        setUploadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    }

    // Offer to close the task once the WHOLE batch has landed - once, not once
    // per file. This is a nudge after work the client already finished, not a
    // gate on the checkbox, which is why its dismiss reads "Not Yet" and not
    // "Cancel": nothing is being cancelled, the files are saved either way.
    if (uploaded > 0 && !isCompleted) {
      if (await askConfirm({
        title: "Mark this task complete?",
        subject: "task",
        body: "will be marked complete.",
        lines: ["Otherwise you will keep getting reminders about it."],
        confirmLabel: "Mark complete",
        cancelLabel: "Not Yet",
      })) {
        completeMutation.mutate(true);
      }
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    handleFiles(e.target.files);
    e.target.value = "";
  }

  function handleCommentSubmit() {
    const trimmed = commentText.trim();
    if (!trimmed) return;
    commentMutation.mutate(trimmed);
  }

  return (
    <>
    <div className="border rounded-lg overflow-hidden">
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors">
        <div
          className="shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isCompleted}
            disabled={completeMutation.isPending}
            onCheckedChange={(checked) => completeMutation.mutate(checked === true)}
            aria-label={isCompleted ? "Mark task incomplete" : "Mark task complete"}
          />
        </div>
        <button
          onClick={() => {
            // Collapsing disarms any pending delete confirm — a destructive
            // control must not still be armed when the row is reopened later.
            setConfirmingDeleteId(null);
            setOpen((o) => !o);
          }}
          className="flex-1 flex items-center gap-3 text-left min-w-0"
        >
          <span className={`flex-1 text-sm font-medium truncate ${isCompleted ? "line-through text-muted-foreground" : ""}`}>
            {task.title}
          </span>
          <div className="flex items-center gap-3 shrink-0 text-muted-foreground">
            {(task.firm_attachment_count ?? 0) > 0 && (
              <span className="flex items-center gap-1 text-xs font-medium text-primary" title="Files shared by your firm">
                <Paperclip className="w-3.5 h-3.5" />
                {task.firm_attachment_count} from your firm
              </span>
            )}
            {task.due_date && (
              <span className="text-xs">{format(parseDate(task.due_date), "MMM d")}</span>
            )}
            {task.allow_client_upload && <Paperclip className="w-3.5 h-3.5" />}
            <MessageSquare className="w-3.5 h-3.5" />
          </div>
        </button>
        {task.allow_client_upload && !isCompleted && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
              // Input renders after the row expands — defer the click one frame
              requestAnimationFrame(() => fileInputRef.current?.click());
            }}
            className="shrink-0 flex items-center gap-1 rounded-md bg-primary text-primary-foreground text-xs font-semibold px-2.5 py-1.5 hover:bg-primary/90 transition-colors"
            title="Upload documents for this task"
          >
            <Upload className="w-3.5 h-3.5" />
            Upload
          </button>
        )}
      </div>

      {open && (
        <div className="border-t bg-muted/20 px-4 py-3 space-y-4">
          {/* Updates from firm */}
          {comments && comments.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Updates</p>
              {comments.map((c) => (
                <div key={c.id} className="bg-card border rounded px-3 py-2 text-sm space-y-1">
                  <p>{c.body}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.author_name ?? "You"} · {format(new Date(c.created_at), "MMM d, yyyy 'at' h:mm a")}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Files shared by the firm — first thing under Updates, above the
              client's own documents/composer. Always visible regardless of
              upload permission. (ENG-60: was buried below the composer.) */}
          {attachments && attachments.some((a) => a.from_firm) && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">From Your Firm</p>
              <ul className="space-y-1.5">
                {attachments.filter((a) => a.from_firm).map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm">
                    <Paperclip className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="truncate flex-1">{a.name}</span>
                    {a.file_size && (
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatFileSize(a.file_size)}
                      </span>
                    )}
                    {isViewable(a) && (
                      <button
                        onClick={() => viewMutation.mutate(a.id)}
                        disabled={viewMutation.isPending}
                        className="shrink-0 inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium hover:bg-muted transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>
                    )}
                    <button
                      onClick={() => downloadMutation.mutate(a.id)}
                      disabled={downloadMutation.isPending}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-2 py-1 text-xs font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Attachments */}
          {!task.allow_client_upload && (
            <p className="text-xs text-muted-foreground mt-1 italic">
              This task doesn't accept file uploads — if you need to send us a file, mention it in a message below.
            </p>
          )}
          {task.allow_client_upload && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Your Documents</p>
              {attachments && attachments.some((a) => !a.from_firm) && (
                <ul className="space-y-1">
                  {attachments.filter((a) => !a.from_firm).map((a) => (
                    <li key={a.id} className="flex items-center gap-2 text-sm">
                      <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{a.name}</span>
                      {a.file_size && (
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatFileSize(a.file_size)}
                        </span>
                      )}
                      <button
                        onClick={() => downloadMutation.mutate(a.id)}
                        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                        title="Download"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {confirmingDeleteId === a.id ? (
                        <span className="shrink-0 flex items-center gap-1.5 text-xs">
                          <span className="text-muted-foreground">Delete permanently?</span>
                          <button
                            onClick={() => deleteMutation.mutate(a.id)}
                            disabled={deleteMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-md bg-destructive px-2 py-0.5 text-destructive-foreground font-semibold hover:bg-destructive/90 transition-colors disabled:opacity-60"
                          >
                            {deleteMutation.isPending && deleteMutation.variables === a.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Delete"
                            )}
                          </button>
                          <button
                            onClick={() => setConfirmingDeleteId(null)}
                            disabled={deleteMutation.isPending}
                            className="rounded-md border px-2 py-0.5 hover:bg-muted transition-colors disabled:opacity-60"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setConfirmingDeleteId(a.id)}
                          className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                          title={`Delete ${a.name}`}
                          aria-label={`Delete ${a.name}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.csv,.zip,.qbb,.qbw,.qbx,.qbo,.qbm,.txt,.heic"
                  onChange={handleFileChange}
                />
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); } }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                  className={`flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${
                    dragOver
                      ? "border-primary bg-primary/10"
                      : "border-primary/40 bg-primary/5 hover:border-primary hover:bg-primary/10"
                  }`}
                >
                  {uploadingKeys.size > 0 ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <p className="text-sm font-medium">
                        Uploading{uploadingKeys.size > 1 ? ` (${uploadingKeys.size} left)` : "…"}
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-primary" />
                      <p className="text-sm font-semibold">Upload your documents here</p>
                      <p className="text-xs text-muted-foreground">Drag &amp; drop files, or click to browse</p>
                    </>
                  )}
                </div>
                {uploadResults.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {uploadResults.map((r) => (
                      <li
                        key={r.key}
                        className={`flex items-center gap-1.5 text-xs ${
                          r.status === "success"
                            ? "text-green-600"
                            : r.status === "duplicate"
                            ? "text-amber-600"
                            : "text-destructive"
                        }`}
                      >
                        {r.status === "success" && <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />}
                        {r.status !== "success" && <AlertTriangle className="w-3.5 h-3.5 shrink-0" />}
                        <span className="truncate">
                          {r.status === "success" && `Uploaded ${r.name}`}
                          {r.status === "duplicate" && `Already uploaded ${r.name} — skipped`}
                          {r.status === "error" && `Failed to upload ${r.name}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Send a message */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Send a message</p>
            <Textarea
              placeholder="Type a message or question…"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={3}
              className="text-sm resize-none"
            />
            <Button
              size="sm"
              onClick={handleCommentSubmit}
              disabled={!commentText.trim() || commentMutation.isPending}
            >
              {commentMutation.isPending ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Sending…</>
              ) : (
                "Send"
              )}
            </Button>
          </div>

        </div>
      )}
    </div>
    </>
  );
}

export default function PortalProject() {
  const [, params] = useRoute("/portal/project/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ?? "";

  const { data: me, error: meError } = useQuery({
    queryKey: ["portal", "me"],
    queryFn: () => portalApi.me(),
  });

  // Redirect to login (with returnTo) if not authenticated
  if (meError) {
    const returnTo = encodeURIComponent(`/portal/project/${id}`);
    setLocation(`/portal/login?returnTo=${returnTo}`);
  }

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ["portal", "project", id],
    queryFn: () => portalApi.project(id),
    enabled: !!id,
  });

  const tasksQueryKey = ["portal", "tasks", id];
  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: tasksQueryKey,
    queryFn: () => portalApi.tasks(id),
    enabled: !!id,
  });

  const isLoading = projectLoading || tasksLoading;
  const chatUnread = useChatUnreadCount();

  return (
    <PortalLayout accountName={me?.account_name}>
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => setLocation("/portal")} className="-ml-2">
          <ChevronLeft className="w-4 h-4 mr-1" />
          All projects
        </Button>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading…</span>
          </div>
        )}

        {project && (
          <>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <h1 className="text-2xl font-bold flex-1">{project.title}</h1>
                <Badge variant="secondary" className="mt-1 shrink-0 capitalize">
                  {project.status.replace(/_/g, " ")}
                </Badge>
              </div>
              {project.period && (
                <p className="text-muted-foreground text-sm font-medium">{project.period}</p>
              )}
              {project.due_date && (
                <p className="text-muted-foreground text-sm">
                  Due {format(parseDate(project.due_date), "MMMM d, yyyy")}
                </p>
              )}
              {project.description && (
                <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
              )}
            </div>

            <Tabs defaultValue="tasks">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 mb-4 -mx-0">
                <TabsTrigger
                  value="tasks"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Tasks
                </TabsTrigger>
                <TabsTrigger
                  value="messages"
                  className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-semibold data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground text-muted-foreground"
                >
                  Messages
                  {chatUnread > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold min-w-[18px] h-[18px] px-1">
                      {chatUnread}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="tasks" className="mt-0">
                <div className="space-y-3">
                  {!tasksLoading && (!tasks || tasks.length === 0) && (
                    <p className="text-sm text-muted-foreground">No tasks assigned yet.</p>
                  )}
                  {(tasks || []).map((task) => (
                    <TaskRow key={task.id} task={task} projectQueryKey={tasksQueryKey} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="messages" className="mt-0">
                <ClientChat />
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </PortalLayout>
  );
}
